import { Container, Link, useTheme } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppButton,
  AppGridContainer,
  AppGridContent,
  AppTypography,
  StackColumn,
  StackRow,
} from 'src/components';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from 'src/components/navigation-menu';
import { SidebarItemProps } from './sidebar/sidebar';
import { CubesIcon } from 'src/theme/overrides/CustomIcons';
import useMerchant from 'src/hooks/useMerchant';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { CreateProgramme } from 'src/pages/programmes/components/create-programme';
import TextMaxLine from 'src/components/TextMaxLine';
import { IconExternalLink, IconLock } from 'src/libraries/icons';
import { getActive, hasActiveChild, isExternalLink } from './sidebar/utils';
import { NavItemAction } from 'src/layouts/dashboard/navbar/NavConfig';
import { ProgrammeOnboardingDialog } from 'src/components/dialogs/programme-onboarding';
import useSubscriptions from 'src/hooks/useSubscriptions';
import { useToggle } from 'src/hooks/useToggle';

//
//

export const AppNavigationMenu = ({ navConfig }: { navConfig: SidebarItemProps[] }) => {
  const { merchant } = useMerchant();
  const { currentPlan } = useSubscriptions();
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const guidanceToggle = useToggle();

  const menuToDisplay = navConfig?.slice(0, 3);

  return (
  <>
    <NavigationMenu>
      <NavigationMenuList>
        {menuToDisplay?.map((item, idx) => {
          const isItemActive =
            getActive(item.path, pathname) ||
            hasActiveChild(item.children, pathname) ||
            (item.name === 'programmes' && getActive(PATH_DASHBOARD.programs.root, pathname));

          return (
            <NavigationMenuItem key={idx}>
              <NavigationMenuTrigger data-active={isItemActive || undefined}>
                {item?.title}
              </NavigationMenuTrigger>

              <NavigationMenuContent>
                <Container maxWidth="sm">
                  <AppGridContainer>
                    <AppGridContent size={6.5} display="flex">
                      <StackColumn gap={0} sx={{ width: 200 }} flex={1}>
                        {item?.name === 'programmes' ? (
                          <StackColumn justifyContent="space-between" flex={1}>
                            <StackColumn gap={0}>
                              {item?.children?.slice(0, 5)?.map((programme: any, idx: number) => {
                                return (
                                  <NavigationMenuLink
                                    key={idx}
                                    closeOnClick
                                    active={getActive(programme?.defaultPath, pathname)}
                                    render={
                                      <Link
                                        component={RouterLink}
                                        to={programme?.defaultPath}
                                        underline="none"
                                        color="inherit"
                                      />
                                    }
                                  >
                                    <TextMaxLine line={1} variant="body2">
                                      {programme?.title}
                                    </TextMaxLine>
                                  </NavigationMenuLink>
                                );
                              })}

                              {merchant && !item?.children?.length && (
                                <AppTypography variant="body2" color="text.secondary">
                                  There are no active programmes.
                                </AppTypography>
                              )}
                            </StackColumn>

                            <StackRow px={1}>
                              <NavigationMenuLink
                                unstyled
                                closeOnClick
                                render={
                                  <AppButton
                                    variant="text"
                                    sx={{ p: 0, color: 'inherit', fontWeight: 500 }}
                                    onClick={() => navigate(PATH_DASHBOARD.programs.root)}
                                  >
                                    {merchant && item?.children?.length
                                      ? 'All programmes'
                                      : 'Visit Programmes page'}
                                  </AppButton>
                                }
                              />

                              <NavigationMenuLink
                                unstyled
                                closeOnClick
                                render={
                                  <CreateProgramme
                                    variant="text"
                                    sx={{ p: 0, fontWeight: 500 }}
                                    text="Create programme"
                                  />
                                }
                              />
                            </StackRow>
                          </StackColumn>
                        ) : (
                          item?.children?.map((ch: any, idx: number) => {
                            if (ch?.action === NavItemAction.guidance) {
                              return (
                                <NavigationMenuLink
                                  key={idx}
                                  closeOnClick
                                  render={
                                    <Link
                                      component="button"
                                      underline="none"
                                      color="inherit"
                                      sx={{ textAlign: 'left' }}
                                      onClick={guidanceToggle.on}
                                    />
                                  }
                                >
                                  {ch?.title}
                                </NavigationMenuLink>
                              );
                            }

                            return (
                              <NavigationMenuLink
                                key={idx}
                                closeOnClick
                                active={getActive(ch?.path, pathname)}
                                // aria-disabled={ch?.disabled}
                                render={
                                  isExternalLink(ch?.path) ? (
                                    <Link
                                      href={ch.path}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="none"
                                      color="inherit"
                                    />
                                  ) : (
                                    <Link
                                      component={RouterLink}
                                      to={ch?.path}
                                      underline="none"
                                      color="inherit"
                                    />
                                  )
                                }
                              >
                                <StackRow justifyContent="space-between" flex={1}>
                                  <StackRow gap={1}>
                                    {ch?.title}
                                    {ch?.disabled && <IconLock sx={{ height: 14 }} />}
                                  </StackRow>

                                  {isExternalLink(ch?.path) && (
                                    <IconExternalLink
                                      size={14}
                                      color={theme.palette.text.secondary}
                                    />
                                  )}
                                </StackRow>
                              </NavigationMenuLink>
                            );
                          })
                        )}
                      </StackColumn>
                    </AppGridContent>

                    <AppGridContent size={5.5}>
                      <StackColumn
                        gap={2}
                        p={2}
                        pb={3}
                        sx={{ background: (theme) => theme.palette.primary.lighter }}
                      >
                        <StackColumn gap={1}>
                          <CubesIcon
                            sx={{
                              '& .primary': { fill: (theme) => theme.palette.branded.carbon },
                              '& .secondary': { fill: (theme) => theme.palette.primary.main },
                            }}
                          />
                          <AppTypography variant="body1" fontWeight="bold">
                            {item?.title}
                          </AppTypography>
                        </StackColumn>

                        <AppTypography variant="body2" color="#4a4340">{item?.description}</AppTypography>
                      </StackColumn>
                    </AppGridContent>
                  </AppGridContainer>
                </Container>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>

      {currentPlan?.packageCode && (
        <ProgrammeOnboardingDialog
          open={guidanceToggle.value}
          onClose={guidanceToggle.off}
          packageCode={currentPlan.packageCode}
        />
      )}
    </>
  );
};
